import React from 'react';

import { mergeBodyHTMLForSave } from '~/components/document-editor/editor-document';

export type EditorSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

/**
 * Apollo context for the request behind `onUpdate`. Saves go one at a time, so a request that
 * never answers would hold every later save; aborting it becomes the hook's error and blur retry.
 */
export const editorSaveContext = () => {
  const controller = new AbortController();
  // A plain timer rather than AbortSignal.timeout, which test clocks cannot advance. Aborting a
  // request that already answered does nothing.
  setTimeout(
    () => controller.abort(new DOMException('The save took too long.', 'TimeoutError')),
    30_000
  );
  return { fetchOptions: { signal: controller.signal } };
};

/**
 * Debounced save, full-HTML prefix/suffix merge, and refs that track editor vs.
 * persisted content. Shared by workspace artifacts, the documents tab, and
 * library template body editing.
 */
export function useEditorDocumentPersistence(params: {
  content: string;
  onUpdate: (content: string) => void | Promise<void>;
  onSaveStatusChange?: (status: EditorSaveStatus) => void;
  /** The full document each time a save lands. */
  onSaved?: (content: string) => void;
}) {
  const { content, onUpdate, onSaveStatusChange, onSaved } = params;

  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const lastContentRef = React.useRef<string>(content);
  const lastSavedContentRef = React.useRef<string>(content);
  const incomingContentRef = React.useRef(content);
  incomingContentRef.current = content;
  const isInitialMountRef = React.useRef<boolean>(true);
  const savedTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  /** Ignore stale workspace subscription payloads until the saved doc echoes back. */
  const awaitingSaveEchoRef = React.useRef<boolean>(false);
  /** Lets blur retry a rejected latest save even when the editor content has not changed. */
  const needsRetryRef = React.useRef(false);
  /**
   * Saves queued or sent but not yet answered. Each is sent after the one before it settles:
   * the server applies saves in the order they arrive, so two in flight at once could land the
   * older edit last. Until they settle, matching the last answered save says nothing about what
   * the server will hold.
   */
  const pendingSavesRef = React.useRef(0);
  const saveQueueRef = React.useRef<Promise<void>>(Promise.resolve());
  const saveCountRef = React.useRef(0);
  /**
   * The newest save queued for each document epoch, so one with a newer save for the same
   * document behind it can skip the request.
   */
  const latestSavesRef = React.useRef(new Map<number, number>());
  /**
   * Advanced when the editor stops showing the document its queued saves belong to (unmount, or
   * another artifact or version). Those saves are still sent, but no longer report status or
   * set the saved baseline of the document now shown.
   */
  const documentEpochRef = React.useRef(0);

  const documentPrefixRef = React.useRef<string | null>(null);
  const documentSuffixRef = React.useRef<string | null>(null);

  const getContentToSave = React.useCallback(
    (bodyHTML: string) =>
      mergeBodyHTMLForSave(bodyHTML, documentPrefixRef.current, documentSuffixRef.current),
    []
  );

  /** Sends the edit still waiting out its debounce, so leaving the document does not drop it. */
  const sendPendingEditRef = React.useRef<(() => void) | null>(null);
  /**
   * What this editor recently sent to the document it shows, so the echo of an earlier save that
   * lands after the next one is sent is not taken for someone else's write. Echoes arrive in the
   * order the saves did, soon after them, so a short list is enough.
   */
  const recentlySentRef = React.useRef<string[]>([]);
  /** Changes when the last pending save settles: content sync waits for that to load what arrived. */
  const [savesSettled, setSavesSettled] = React.useState(0);

  const sendSave = React.useCallback(
    async (contentToSave: string, save: number, epoch: number) => {
      const shown = () => epoch === documentEpochRef.current;
      const superseded = () => latestSavesRef.current.get(epoch) !== save;
      const latest = () => !superseded() && timeoutRef.current === null;
      // The newer save behind this one carries the whole document.
      if (superseded()) return;
      if (shown()) onSaveStatusChange?.('saving');
      const incomingWhenSent = incomingContentRef.current;
      try {
        await onUpdate(contentToSave);
        if (!shown()) return;
        lastSavedContentRef.current = contentToSave;
        if (!latest()) return;
        needsRetryRef.current = false;
        // A document that arrived while the save was out, and is not one this editor sent, was
        // written by someone else after it: content sync loads it once the saves settle. An echo of
        // an earlier save is still waited out, or it would replace the text this save holds.
        const incoming = incomingContentRef.current;
        awaitingSaveEchoRef.current =
          incoming !== contentToSave &&
          (incoming === incomingWhenSent || recentlySentRef.current.includes(incoming));
        onSaved?.(contentToSave);
        onSaveStatusChange?.('saved');
        if (savedTimeoutRef.current) {
          clearTimeout(savedTimeoutRef.current);
        }
        savedTimeoutRef.current = setTimeout(() => {
          onSaveStatusChange?.('idle');
        }, 2000);
      } catch {
        if (shown() && latest()) {
          needsRetryRef.current = true;
          onSaveStatusChange?.('error');
        }
      }
    },
    [onSaved, onSaveStatusChange, onUpdate]
  );

  const executeSave = React.useCallback(
    (contentToSave: string) => {
      recentlySentRef.current = [...recentlySentRef.current.slice(-19), contentToSave];
      const save = ++saveCountRef.current;
      latestSavesRef.current.set(documentEpochRef.current, save);
      const send = sendSave.bind(null, contentToSave, save, documentEpochRef.current);
      // Sent at once when nothing is ahead of it, so a lone save is not deferred a tick.
      const queued = pendingSavesRef.current === 0 ? send() : saveQueueRef.current.then(send);
      pendingSavesRef.current += 1;
      saveQueueRef.current = queued.finally(() => {
        pendingSavesRef.current -= 1;
        if (pendingSavesRef.current === 0) {
          latestSavesRef.current.clear();
          setSavesSettled((n) => n + 1);
        }
      });
      return saveQueueRef.current;
    },
    [sendSave]
  );

  const debouncedOnUpdate = React.useCallback(
    (bodyHTML: string) => {
      awaitingSaveEchoRef.current = false;
      onSaveStatusChange?.('saving');

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      if (savedTimeoutRef.current) {
        clearTimeout(savedTimeoutRef.current);
        savedTimeoutRef.current = null;
      }

      // Merged now: by the time a document is left, its prefix may already be the next one's.
      const contentToSave = getContentToSave(bodyHTML);
      const send = () => {
        timeoutRef.current = null;
        sendPendingEditRef.current = null;
        void executeSave(contentToSave);
      };
      sendPendingEditRef.current = send;
      timeoutRef.current = setTimeout(send, 300);
    },
    [executeSave, getContentToSave, onSaveStatusChange]
  );

  const flushSave = React.useCallback(
    async (bodyHTML: string) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
        sendPendingEditRef.current = null;
      }
      if (savedTimeoutRef.current) {
        clearTimeout(savedTimeoutRef.current);
        savedTimeoutRef.current = null;
      }

      await executeSave(getContentToSave(bodyHTML));
    },
    [executeSave, getContentToSave]
  );

  const sendPendingEdit = React.useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    sendPendingEditRef.current?.();
  }, []);

  /** Drops the edit waiting out its debounce: the reader undid back to what is saved. */
  const cancelPendingSave = React.useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
      sendPendingEditRef.current = null;
    }
    if (savedTimeoutRef.current) {
      clearTimeout(savedTimeoutRef.current);
      savedTimeoutRef.current = null;
    }
    documentEpochRef.current += 1;
    awaitingSaveEchoRef.current = false;
    needsRetryRef.current = false;
    onSaveStatusChange?.('idle');
  }, [onSaveStatusChange]);

  /** The editor moved to another version: its last edit is still saved to the one it left. */
  const leaveDocument = React.useCallback(() => {
    sendPendingEdit();
    if (savedTimeoutRef.current) {
      clearTimeout(savedTimeoutRef.current);
      savedTimeoutRef.current = null;
    }
    documentEpochRef.current += 1;
    recentlySentRef.current = [];
    awaitingSaveEchoRef.current = false;
    needsRetryRef.current = false;
    onSaveStatusChange?.('idle');
  }, [onSaveStatusChange, sendPendingEdit]);

  React.useEffect(() => {
    return () => {
      sendPendingEdit();
      if (savedTimeoutRef.current) {
        clearTimeout(savedTimeoutRef.current);
      }
      documentEpochRef.current += 1;
      awaitingSaveEchoRef.current = false;
      needsRetryRef.current = false;
    };
  }, [sendPendingEdit]);

  return {
    documentPrefixRef,
    documentSuffixRef,
    lastContentRef,
    lastSavedContentRef,
    isInitialMountRef,
    awaitingSaveEchoRef,
    needsRetryRef,
    pendingSavesRef,
    savesSettled,
    hasUnsentEdit: () => timeoutRef.current !== null,
    getContentToSave,
    debouncedOnUpdate,
    flushSave,
    cancelPendingSave,
    leaveDocument
  };
}
